import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  ContributorMatchFilter as ContributorMatchFilterModel,
  ContributorMatchListDto,
  ContributorMatchListFilter,
  ContributorMatchListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { ContributorMatchFilter } from "../ContributorMatchFilter";
import { contributorMatchPageRoute } from "../ContributorMatchPage/ContributorMatchPage";
import { ContributorMatchTable } from "./ContributorMatchTable";

const routeName = "contributorMatches";

export const contributorMatchListPageRoute: PageRoute = {
  handle: "Time & Effort",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const ContributorMatchListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.ContributorMatch;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<ContributorMatchListFilter>({
    sortObjects: [{ property: pn<ContributorMatchListDto>("date"), isDesc: true }],
  });
  const [initListFilter] = useState<ContributorMatchListFilter>(listFilter);
  const [listItems, setListItems] = useState<ContributorMatchListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<ContributorMatchListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.contributorMatches.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.contributorMatches.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.contributorMatches.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<ContributorMatchListFilter>({
    maxWidth: "md",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <ContributorMatchFilter
        filter={listFilter.matchFilter || ({} as ContributorMatchFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.matchFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.contributorMatches.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{contributorMatchListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate({
                      pathname: contributorMatchPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "standard" }),
                    })
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <ContributorMatchTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              contributorMatches={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
